import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { money, fmtDate, fmtDateTime } from "@/lib/api";
import { FENDER_LOGO_DATA_URL } from "@/lib/logoData";

// Brand palette (matches CSS variables)
const DEEP_SPACE = [29, 53, 87];   // #1d3557
const STRAWBERRY = [230, 57, 70];  // #e63946
const STEEL = [69, 123, 157];      // #457b9d
const FROSTED = [168, 218, 220];   // #a8dadc
const MUTED = [113, 128, 145];
const ZINC = DEEP_SPACE;            // legacy alias
const AMBER = STRAWBERRY;           // legacy alias

const applyBranding = (doc, garage) => {
  // Support both A4 (210) and A5 (148) page widths dynamically.
  const pageW = doc.internal.pageSize.getWidth();
  const rightX = pageW - 8;   // Fender wordmark right anchor
  const logoX = rightX - 30;  // approx position for the F-mark

  // Light frosted header band so the deep-space logo stays visible.
  doc.setFillColor(241, 250, 238); // #F1FAEE honeydew — pairs with brand palette
  doc.rect(0, 0, pageW, 30, "F");
  // Strawberry accent strip on the bottom of the band
  doc.setFillColor(...STRAWBERRY);
  doc.rect(0, 30, pageW, 1.5, "F");

  // ------ LEFT: Garage identity (workshop that issues the document) ------
  const g = garage || {};
  const cityLine = [g.city, g.state, g.pin_code].filter(Boolean).join(", ");
  const contactLine = [g.phone, g.email].filter(Boolean).join(" · ");
  const maxLeft = Math.max(60, pageW - 55); // reserve room for right-side branding

  doc.setTextColor(...DEEP_SPACE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(pageW < 160 ? 11 : 13);
  doc.text(String(g.name || "Your Garage").slice(0, 60), 8, 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(pageW < 160 ? 7 : 8);
  doc.setTextColor(...STEEL);
  let y = 15;
  if (g.address)    { doc.text(String(g.address).slice(0, 90), 8, y, { maxWidth: maxLeft }); y += 4; }
  if (cityLine)     { doc.text(cityLine.slice(0, 90), 8, y, { maxWidth: maxLeft });          y += 4; }
  if (contactLine)  { doc.text(contactLine.slice(0, 90), 8, y, { maxWidth: maxLeft });        y += 4; }
  if (g.gst_number) { doc.text(`GSTIN: ${g.gst_number}`, 8, y);                              y += 4; }

  // ------ RIGHT: Fender by SparesHub branding ------
  try {
    doc.addImage(FENDER_LOGO_DATA_URL, "PNG", logoX, 5, 10, 8);
  } catch (e) {
    /* logo embedding is best-effort */
  }
  doc.setTextColor(...DEEP_SPACE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(pageW < 160 ? 11 : 13);
  doc.text("Fender", rightX, 11, { align: "right" });
  doc.setFontSize(pageW < 160 ? 6 : 7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...STEEL);
  doc.text("by SparesHub", rightX, 16, { align: "right" });
  doc.text("fenderapp.com", rightX, 20, { align: "right" });
  doc.setFontSize(pageW < 160 ? 5 : 6);
  doc.setTextColor(...MUTED);
  doc.text("POWERING WORKSHOPS. DRIVING GROWTH.", rightX, 24, { align: "right" });

  doc.setTextColor(0, 0, 0);
};

const drawFooter = (doc, page) => {
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...FROSTED);
  doc.setLineWidth(0.3);
  doc.line(10, h - 12, 200, h - 12);
  try {
    doc.addImage(FENDER_LOGO_DATA_URL, "PNG", 10, h - 10, 5, 4);
  } catch (e) {
    /* logo embedding is best-effort */
  }
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.setFont("helvetica", "normal");
  doc.text("Fender by SparesHub · fenderapp.com · © 2026 Iradium Automobiles Private Limited", 17, h - 7);
  if (page) doc.text(`Page ${page}`, 200, h - 7, { align: "right" });
};

const sectionLabel = (doc, y, label) => {
  doc.setFontSize(8);
  doc.setTextColor(...STEEL);
  doc.setFont("helvetica", "bold");
  doc.text(label.toUpperCase(), 10, y);
  doc.setDrawColor(...STRAWBERRY);
  doc.setLineWidth(0.4);
  doc.line(10, y + 1, 200, y + 1);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
};

export function jobCardPdf(jc) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const garage = jc._garage || {};
  applyBranding(doc, garage);

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("JOB CARD", 10, 38);
  doc.setFontSize(12);
  doc.setTextColor(...STRAWBERRY);
  doc.text(jc.job_number || "", 10, 45);
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Checked in: ${fmtDateTime(jc.checkin_at || jc.created_at)}`, 200, 38, { align: "right" });
  doc.text(`Status: ${String(jc.status).replace(/_/g, " ").toUpperCase()}`, 200, 43, { align: "right" });

  let y = 56;
  sectionLabel(doc, y, "Vehicle & Customer");
  y += 8;
  doc.setFontSize(10);
  doc.text(`${jc.vehicle?.make || ""} ${jc.vehicle?.model || ""}  ·  ${jc.vehicle?.reg_number || ""}`, 10, y);
  y += 5;
  doc.text(`Year: ${jc.vehicle?.year || "-"}   VIN: ${jc.vehicle?.vin || "-"}   Odometer: ${jc.odometer || 0} km`, 10, y);
  y += 6;
  doc.text(`Customer: ${jc.customer?.name || "-"}   ${jc.customer?.phone || ""}`, 10, y);

  y += 10;
  sectionLabel(doc, y, "Complaints");
  y += 8;
  const complaintLines = doc.splitTextToSize(jc.complaints || "-", 190);
  doc.text(complaintLines, 10, y);
  y += complaintLines.length * 5 + 4;

  if ((jc.tasks || []).length > 0) {
    sectionLabel(doc, y, "Tasks");
    y += 8;
    doc.setFontSize(9);
    (jc.tasks || []).forEach((t, i) => {
      doc.text(`${i + 1}. ${t.task_name}`, 10, y);
      doc.setTextColor(...MUTED);
      doc.text(`${t.status}   ${t.hours_est || 0}h @ Rs. ${t.labor_rate || 0}/hr`, 200, y, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 6;
      if (y > 280) { doc.addPage(); y = 20; }
    });
    y += 2;
  }

  if (jc.inspection && (jc.inspection.checklist?.length || jc.inspection.damages?.length)) {
    if (y > 240) { doc.addPage(); y = 20; }
    sectionLabel(doc, y, "Inspection");
    y += 8;
    doc.setFontSize(9);
    (jc.inspection.checklist || []).forEach((c) => {
      doc.text(`• ${c.label} — ${c.status.toUpperCase()}${c.note ? ` — ${c.note}` : ""}`, 10, y);
      y += 5;
      if (y > 285) { doc.addPage(); y = 20; }
    });
    (jc.inspection.damages || []).forEach((d) => {
      doc.text(`• Damage (${d.view}, ${d.severity}) — ${d.note || ""}`, 10, y);
      y += 5;
      if (y > 285) { doc.addPage(); y = 20; }
    });
  }

  const est = (jc.estimates || [])[0];
  if (est) {
    if (y > 240) { doc.addPage(); y = 20; }
    sectionLabel(doc, y, `Estimate v${est.version} · ${est.status.toUpperCase()}`);
    y += 8;
    doc.setFontSize(9);
    (est.lines || []).forEach((l) => {
      doc.text(`${l.description}`, 10, y);
      doc.text(`${l.qty} × Rs. ${money(l.unit_price)}`, 140, y, { align: "right" });
      doc.text(`Rs. ${money(l.qty * l.unit_price)}`, 200, y, { align: "right" });
      y += 5;
      if (y > 285) { doc.addPage(); y = 20; }
    });
    y += 3;
    doc.text(`Subtotal`, 140, y, { align: "right" });
    doc.text(`Rs. ${money(est.subtotal)}`, 200, y, { align: "right" }); y += 5;
    doc.text(`Tax`, 140, y, { align: "right" });
    doc.text(`Rs. ${money(est.tax_amount)}`, 200, y, { align: "right" }); y += 5;
    doc.setFont("helvetica", "bold");
    doc.text(`TOTAL`, 140, y, { align: "right" });
    doc.text(`Rs. ${money(est.total)}`, 200, y, { align: "right" });
    doc.setFont("helvetica", "normal");
  }

  drawFooter(doc);

  return doc;
}

export function invoicePdf(inv, jc) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const garage = jc?._garage || {};
  applyBranding(doc, garage);

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("TAX INVOICE", 10, 38);
  doc.setFontSize(12);
  doc.setTextColor(...STRAWBERRY);
  doc.text(inv.invoice_number || "", 10, 45);
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${fmtDate(inv.created_at)}`, 200, 38, { align: "right" });
  doc.text(`Status: ${String(inv.status).toUpperCase()}`, 200, 43, { align: "right" });

  let y = 56;
  sectionLabel(doc, y, "Bill To");
  y += 8;
  doc.setFontSize(10);
  doc.text(`${jc?.customer?.name || "-"}`, 10, y); y += 5;
  doc.text(`${jc?.customer?.phone || ""}`, 10, y); y += 5;
  if (jc?.customer?.address) { doc.text(jc.customer.address, 10, y); y += 5; }

  y += 4;
  sectionLabel(doc, y, "Vehicle");
  y += 8;
  doc.text(`${jc?.vehicle?.make || ""} ${jc?.vehicle?.model || ""}  ·  ${jc?.vehicle?.reg_number || ""}`, 10, y);
  y += 8;

  sectionLabel(doc, y, "Items");
  y += 8;
  doc.setFontSize(9);
  (inv.lines || []).forEach((l) => {
    doc.text(`${l.description}`, 10, y);
    doc.text(`${l.qty} × Rs. ${money(l.unit_price)}`, 140, y, { align: "right" });
    doc.text(`Rs. ${money(l.qty * l.unit_price)}`, 200, y, { align: "right" });
    y += 5;
    if (y > 265) { doc.addPage(); y = 20; }
  });

  y += 4;
  doc.text("Subtotal", 140, y, { align: "right" });
  doc.text(`Rs. ${money(inv.subtotal)}`, 200, y, { align: "right" }); y += 5;
  doc.text("Tax", 140, y, { align: "right" });
  doc.text(`Rs. ${money(inv.tax_amount)}`, 200, y, { align: "right" }); y += 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("TOTAL", 140, y, { align: "right" });
  doc.text(`Rs. ${money(inv.total)}`, 200, y, { align: "right" }); y += 8;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...MUTED);
  doc.text(`Paid: Rs. ${money(inv.paid_amount)}   Balance: Rs. ${money(inv.balance)}`, 200, y, { align: "right" });

  drawFooter(doc);
  return doc;
}

export function estimatePdf(est, garage) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  applyBranding(doc, garage || {});

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DEEP_SPACE);
  doc.text("ESTIMATE", 10, 38);
  doc.setFontSize(12);
  doc.setTextColor(...STRAWBERRY);
  doc.text(est.number || "DRAFT", 10, 45);
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${est.estimate_date || "-"}`, 200, 38, { align: "right" });
  doc.text(`Valid Until: ${est.valid_until || "-"}`, 200, 43, { align: "right" });
  doc.text(`Status: ${String(est.status || "draft").toUpperCase()}`, 200, 48, { align: "right" });

  let y = 58;
  const cust = est.customer_snapshot || {};
  const veh = est.vehicle_snapshot || {};

  // Two-column: customer + vehicle
  sectionLabel(doc, y, "Customer");
  y += 6;
  doc.setFontSize(10);
  doc.text(`${cust.name || "-"}`, 10, y);
  doc.setFontSize(9);
  doc.text(`${cust.phone || ""}`, 10, y + 4);
  if (cust.email) doc.text(cust.email, 10, y + 8);
  if (cust.address) doc.text(cust.address.slice(0, 60), 10, y + 12);
  if (cust.gstin) doc.text(`GSTIN: ${cust.gstin}`, 10, y + 16);

  // Vehicle column
  doc.setFontSize(8);
  doc.setTextColor(...STEEL);
  doc.setFont("helvetica", "bold");
  doc.text("VEHICLE", 110, y - 6);
  doc.setDrawColor(...STRAWBERRY);
  doc.line(110, y - 5, 200, y - 5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(`${veh.reg_number || "-"}`, 110, y);
  doc.setFontSize(9);
  doc.text(`${[veh.make, veh.model, veh.variant].filter(Boolean).join(" ")}`, 110, y + 4);
  if (veh.fuel_type || veh.year) doc.text(`${[veh.fuel_type, veh.year].filter(Boolean).join(" · ")}`, 110, y + 8);
  if (veh.odometer) doc.text(`Odometer: ${veh.odometer} km`, 110, y + 12);
  if (veh.vin) doc.text(`VIN: ${veh.vin}`, 110, y + 16);
  y += 24;

  // Complaints
  const complaints = est.complaints || [];
  if (complaints.length > 0) {
    sectionLabel(doc, y, "Customer Complaints");
    y += 6;
    doc.setFontSize(9);
    complaints.forEach((c) => {
      doc.text(`• ${c.text || ""}`, 12, y);
      y += 4;
      if (y > 265) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
    });
    y += 4;
  }

  const drawLineTable = (title, lines, opts = {}) => {
    if (!lines || lines.length === 0) return;
    if (y > 245) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
    sectionLabel(doc, y, title);
    y += 6;
    // Table header
    doc.setFillColor(241, 250, 238); // honeydew
    doc.rect(10, y - 4, 190, 6, "F");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...DEEP_SPACE);
    doc.text("Item", 12, y);
    doc.text("Qty", 130, y, { align: "right" });
    doc.text("Rate", 155, y, { align: "right" });
    doc.text("Tax", 175, y, { align: "right" });
    doc.text("Amount", 198, y, { align: "right" });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    lines.forEach((l) => {
      const desc = opts.namer(l);
      const optionalTag = l.optional ? " [Optional]" : "";
      doc.setFontSize(9);
      doc.text(`${desc}${optionalTag}`.slice(0, 60), 12, y);
      doc.text(String(l.qty || 0), 130, y, { align: "right" });
      doc.text(`Rs. ${money(l.unit_price)}`, 155, y, { align: "right" });
      doc.text(`${l.tax_pct || 0}%`, 175, y, { align: "right" });
      const total = l.computed?.line_total ?? ((l.qty || 0) * (l.unit_price || 0) * (1 + (l.tax_pct || 0) / 100));
      doc.text(`Rs. ${money(total)}`, 198, y, { align: "right" });
      y += 5;
      if (y > 265) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
    });
    y += 3;
  };

  drawLineTable("Parts", est.parts, { namer: (l) => `${l.name}${l.sku ? " (" + l.sku + ")" : ""}` });
  drawLineTable("Labour Charges", est.labor, { namer: (l) => l.task_name || "" });
  drawLineTable("Other Charges", est.other_charges, { namer: (l) => l.charge_type || "" });

  // Totals block
  if (y > 240) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
  y += 4;
  const t = est.totals || {};
  const rows = [
    ["Parts", t.parts_subtotal],
    ["Labour", t.labor_subtotal],
    ["Other Charges", t.charges_subtotal],
    ["Gross Subtotal", t.gross_subtotal],
    ["Discount", -Number(t.overall_discount || 0)],
    ["Taxable", t.taxable_value],
    (t.igst > 0) ? ["IGST", t.igst] : ["CGST", t.cgst],
    (t.igst > 0) ? null : ["SGST", t.sgst],
    ["Rounding", t.rounding_adjustment],
  ].filter(Boolean);
  doc.setFontSize(9);
  rows.forEach(([label, val]) => {
    doc.setTextColor(...MUTED);
    doc.text(label, 155, y, { align: "right" });
    doc.setTextColor(0, 0, 0);
    doc.text(`Rs. ${money(Math.abs(val || 0))}${Number(val) < 0 ? " -" : ""}`, 198, y, { align: "right" });
    y += 5;
  });
  // Grand Total — no fill background, black text for readability on print.
  y += 1;
  doc.setDrawColor(...DEEP_SPACE);
  doc.setLineWidth(0.4);
  doc.line(120, y - 5, 200, y - 5);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("GRAND TOTAL", 155, y, { align: "right" });
  doc.text(`Rs. ${money(t.grand_total)}`, 198, y, { align: "right" });
  doc.setLineWidth(0.2);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  y += 12;

  // Terms
  const terms = est.terms || [];
  if (terms.length > 0) {
    if (y > 245) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
    sectionLabel(doc, y, "Terms & Conditions");
    y += 6;
    doc.setFontSize(8);
    terms.forEach((tt, i) => {
      const wrapped = doc.splitTextToSize(`${i + 1}. ${tt}`, 188);
      wrapped.forEach((line) => {
        doc.text(line, 12, y);
        y += 3.5;
        if (y > 275) { drawFooter(doc, doc.internal.getNumberOfPages()); doc.addPage(); y = 20; }
      });
    });
  }

  // Customer notes (visible)
  if (est.customer_notes) {
    y += 3;
    sectionLabel(doc, y, "Notes");
    y += 6;
    doc.setFontSize(9);
    const wrapped = doc.splitTextToSize(est.customer_notes, 188);
    wrapped.forEach((line) => { doc.text(line, 12, y); y += 4; });
  }

  drawFooter(doc, doc.internal.getNumberOfPages());
  return doc;
}


export const downloadPdf = (doc, filename) => {
  doc.save(filename);
};

/* =============================================================
 * NEW Job Card PDF (v2) — supports the new 13-state schema,
 * copyType (customer | internal), signature blocks, and rich
 * sections (findings / damage markers / parts / advance / QC).
 * ========================================================== */
export function jobCardPdfV2(jc, { copyType = "customer", garage = null } = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  applyBranding(doc, garage || jc._garage || {});

  const number = jc.number || jc.job_number || "";
  const status = String(jc.status || "").replace(/_/g, " ").toUpperCase();
  const isInternal = copyType === "internal";

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DEEP_SPACE);
  doc.text("JOB CARD", 10, 38);
  doc.setFontSize(12);
  doc.setTextColor(...STRAWBERRY);
  doc.text(number, 10, 45);
  doc.setTextColor(0, 0, 0);

  // Copy-type badge
  doc.setFillColor(isInternal ? 230 : 168, isInternal ? 57 : 218, isInternal ? 70 : 220);
  doc.roundedRect(155, 34, 47, 6, 1.2, 1.2, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text(isInternal ? "INTERNAL COPY (WORKSHOP)" : "CUSTOMER COPY", 178, 38, { align: "center" });
  doc.setTextColor(0, 0, 0);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Received: ${fmtDateTime(jc.checkin_at || jc.created_at)}`, 200, 45, { align: "right" });
  doc.text(`Status: ${status}`, 200, 50, { align: "right" });

  let y = 58;
  // Customer + Vehicle
  sectionLabel(doc, y, "Customer & Vehicle"); y += 6;
  doc.setFontSize(9.5); doc.setFont("helvetica", "bold");
  const cust = jc.customer_snapshot || jc.customer || {};
  const veh  = jc.vehicle_snapshot  || jc.vehicle  || {};
  doc.text(`${cust.name || "-"}`, 10, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${cust.phone || ""}${cust.email ? "  ·  " + cust.email : ""}`, 60, y);
  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text(`${veh.reg_number || "-"}`, 10, y);
  doc.setFont("helvetica", "normal");
  doc.text(`${[veh.make, veh.model, veh.variant].filter(Boolean).join(" ")} ${veh.year ? "(" + veh.year + ")" : ""}`, 60, y);
  doc.text(`Odometer: ${jc.odometer || veh.odometer || 0} km`, 200, y, { align: "right" });
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.text(`VIN: ${veh.vin || "-"}   Fuel: ${jc.fuel_level || veh.fuel_type || "-"}   Engine: ${veh.engine_number || "-"}`, 10, y);
  y += 8;

  // Service + advisor + expected delivery
  sectionLabel(doc, y, "Service Details"); y += 6;
  doc.setFontSize(9);
  doc.text(`Service type: ${jc.service_type || "-"}`, 10, y);
  doc.text(`Repair type: ${(jc.repair_type || "").replace(/_/g, " ")}`, 110, y);
  y += 5;
  doc.text(`Priority: ${jc.priority || "normal"}`, 10, y);
  if (jc.promised_at) doc.text(`Expected delivery: ${fmtDateTime(jc.promised_at)}`, 110, y);
  y += 8;

  // Complaints
  if ((jc.complaints || []).length) {
    sectionLabel(doc, y, "Customer complaints"); y += 6;
    doc.setFontSize(9);
    jc.complaints.filter((c) => isInternal || c.customer_visible !== false).forEach((c, i) => {
      const lines = doc.splitTextToSize(`${i + 1}. ${c.text}${c.category ? " [" + c.category + "]" : ""}`, 190);
      doc.text(lines, 10, y);
      y += lines.length * 4.5 + 1;
      if (y > 275) { doc.addPage(); y = 20; }
    });
    y += 3;
  }

  // Findings — INTERNAL only
  if (isInternal && jc.findings && (jc.findings.initial_observation || jc.findings.probable_cause)) {
    sectionLabel(doc, y, "Service-advisor findings (internal)"); y += 6;
    doc.setFontSize(9);
    if (jc.findings.initial_observation) {
      const l = doc.splitTextToSize(`Initial: ${jc.findings.initial_observation}`, 190);
      doc.text(l, 10, y); y += l.length * 4.5 + 1;
    }
    if (jc.findings.probable_cause) {
      const l = doc.splitTextToSize(`Cause: ${jc.findings.probable_cause}`, 190);
      doc.text(l, 10, y); y += l.length * 4.5 + 1;
    }
    if (jc.findings.recommended_inspection) {
      const l = doc.splitTextToSize(`Recommended: ${jc.findings.recommended_inspection}`, 190);
      doc.text(l, 10, y); y += l.length * 4.5 + 1;
    }
    if (jc.findings.road_test_required) { doc.text("• Road test required", 10, y); y += 4.5; }
    if (jc.findings.immediate_attention) { doc.text("• Immediate attention", 10, y); y += 4.5; }
    y += 3;
    if (y > 265) { doc.addPage(); y = 20; }
  }

  // Tasks
  if ((jc.tasks || []).length) {
    sectionLabel(doc, y, "Selected tasks"); y += 6;
    doc.setFontSize(9);
    jc.tasks.filter((t) => isInternal || t.customer_visible !== false).forEach((t, i) => {
      doc.text(`${i + 1}. ${t.task_name}`, 10, y);
      doc.setTextColor(...MUTED);
      doc.text(`${(t.status || "").replace(/_/g, " ")}${t.hours_est ? "  ·  " + t.hours_est + "h" : ""}`, 200, y, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 5;
      if (y > 275) { doc.addPage(); y = 20; }
    });
    y += 3;
  }

  // Parts
  if ((jc.parts_needed || []).length) {
    sectionLabel(doc, y, "Parts required"); y += 6;
    doc.setFontSize(9);
    jc.parts_needed.forEach((p) => {
      doc.text(`• ${p.name}${p.part_number ? " (" + p.part_number + ")" : ""}`, 10, y);
      doc.setTextColor(...MUTED);
      doc.text(`${p.qty} ${p.unit || "nos"}  ·  ${(p.status || "").replace(/_/g, " ")}`, 200, y, { align: "right" });
      doc.setTextColor(0, 0, 0);
      y += 4.5;
      if (y > 275) { doc.addPage(); y = 20; }
    });
    y += 3;
  }

  // Damage markers summary
  if ((jc.damage_markers || []).length) {
    sectionLabel(doc, y, "Vehicle condition (damage markers)"); y += 6;
    doc.setFontSize(9);
    jc.damage_markers.forEach((d) => {
      doc.text(`• ${(d.view || "").padEnd(6)}  ${d.type || ""}  (${d.severity || ""})  ${d.note || ""}`, 10, y);
      y += 4.5;
      if (y > 275) { doc.addPage(); y = 20; }
    });
    y += 3;
  }

  // Advance
  if (jc.advance?.received && Number(jc.advance.amount) > 0) {
    sectionLabel(doc, y, "Advance payment"); y += 6;
    doc.setFontSize(10); doc.setFont("helvetica", "bold");
    doc.text(`Rs. ${money(jc.advance.amount)}`, 10, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text(`${(jc.advance.mode || "").replace(/_/g, " ")}${jc.advance.reference_number ? "  ·  Ref " + jc.advance.reference_number : ""}${jc.advance.receipt_number ? "  ·  Receipt " + jc.advance.receipt_number : ""}`, 40, y);
    y += 7;
  }

  // Quality Check — INTERNAL only
  if (isInternal && jc.quality_check) {
    if (y > 250) { doc.addPage(); y = 20; }
    sectionLabel(doc, y, "Quality check"); y += 6;
    doc.setFontSize(9);
    const qc = jc.quality_check;
    const rows = [
      ["Work verified", qc.work_verified], ["Parts fitted", qc.parts_fitted],
      ["Fluids checked", qc.fluids_checked], ["Warning lights OK", qc.warning_lights_checked],
      ["Road test completed", qc.road_test_completed], ["Cleaned", qc.cleaned],
      ["Old parts returned", qc.old_parts_returned],
    ];
    rows.forEach(([label, ok]) => {
      doc.text(`${ok ? "[x]" : "[ ]"}  ${label}`, 10, y);
      y += 4.5;
    });
    if (qc.notes) {
      const l = doc.splitTextToSize(`Notes: ${qc.notes}`, 190);
      doc.text(l, 10, y); y += l.length * 4.5;
    }
    y += 3;
  }

  // Terms + Signatures (bottom)
  if (y > 240) { doc.addPage(); y = 20; }
  y = Math.max(y, 240);
  doc.setDrawColor(...FROSTED);
  doc.line(10, y, 200, y);
  y += 6;

  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  const terms = jc.terms || "I hereby authorise the workshop to carry out the repair work listed above. I understand that additional work may be required after inspection and will be quoted separately. The vehicle is left at owner's risk.";
  const tlines = doc.splitTextToSize(terms, 190);
  doc.text(tlines, 10, y);
  y += tlines.length * 3.8 + 6;
  doc.setTextColor(0, 0, 0);

  // Signature blocks
  const sigY = 275;
  doc.setDrawColor(...MUTED);
  doc.line(15, sigY, 80, sigY);
  doc.line(130, sigY, 195, sigY);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("Customer signature", 15, sigY + 4);
  doc.text("Service advisor signature", 130, sigY + 4);
  doc.setTextColor(0, 0, 0);

  drawFooter(doc, 1);
  return doc;
}

export const downloadJobCardPdf = (jc, opts = {}) => {
  const number = jc.number || jc.job_number || "job-card";
  const suffix = opts.copyType === "internal" ? "-internal" : "";
  const doc = jobCardPdfV2(jc, opts);
  doc.save(`${number}${suffix}.pdf`);
};



/* =============================================================
 * BILLING PDFs — Phase 1 helpers for the billing router.
 * ========================================================== */
function rupees(n) {
  const v = Number(n) || 0;
  return `Rs. ${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function invoicePdfV2(inv, garage = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  applyBranding(doc, garage);

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DEEP_SPACE);
  doc.text(inv.invoice_type === "gst" ? "TAX INVOICE" : "INVOICE", 10, 38);

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(`# ${inv.number}`, 10, 46);
  doc.text(`Date: ${(inv.invoice_date || "").slice(0, 10)}`, 10, 52);
  if (inv.job_card_number) doc.text(`Job Card: ${inv.job_card_number}`, 10, 58);

  doc.setFontSize(10);
  doc.text(`Status: ${String(inv.status || "").toUpperCase()}`, 145, 46);
  doc.text(`Payment: ${String(inv.payment_status || "").toUpperCase()}`, 145, 52);
  if (inv.invoice_type === "gst") doc.text(inv.is_interstate ? "IGST" : "CGST+SGST", 145, 58);

  const c = inv.customer_snapshot || {};
  const v = inv.vehicle_snapshot || {};
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("Bill To", 10, 72); doc.text("Vehicle", 110, 72);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(String(c.name || ""), 10, 78);
  doc.text(String(c.phone || ""), 10, 83);
  if (c.address) doc.text(String(c.address).slice(0, 60), 10, 88);
  if (c.gstin) doc.text(`GSTIN: ${c.gstin}`, 10, 93);
  doc.text(String(v.reg_number || ""), 110, 78);
  doc.text([v.make, v.model, v.variant].filter(Boolean).join(" ").slice(0, 40), 110, 83);
  if (v.odometer) doc.text(`Odo: ${v.odometer} km`, 110, 88);

  const rows = (inv.items || []).map((it) => [
    (it.kind || "part").toUpperCase(),
    `${it.name}${it.part_number ? "\n" + it.part_number : ""}`,
    String(it.qty || 0),
    rupees(it.rate),
    it.discount_pct ? `${it.discount_pct}%` : (it.discount_amount ? rupees(it.discount_amount) : "-"),
    inv.invoice_type === "gst" ? `${it.gst_rate || 0}%` : "-",
    rupees(it.total),
  ]);
  autoTable(doc, {
    startY: 105, head: [["Type", "Item", "Qty", "Rate", "Disc", "GST", "Total"]],
    body: rows,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: DEEP_SPACE, textColor: 255, fontStyle: "bold", fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 15 }, 1: { cellWidth: 65 }, 2: { cellWidth: 12, halign: "right" },
      3: { cellWidth: 22, halign: "right" }, 4: { cellWidth: 18, halign: "right" },
      5: { cellWidth: 15, halign: "right" }, 6: { cellWidth: 25, halign: "right" },
    },
    theme: "grid",
  });

  let endY = doc.lastAutoTable.finalY + 6;
  const t = inv.totals || {};
  const totalsBlock = [
    ["Gross subtotal", rupees(t.gross_subtotal)],
    (t.line_discount ? ["Line discount", `- ${rupees(t.line_discount)}`] : null),
    (t.invoice_discount ? ["Invoice discount", `- ${rupees(t.invoice_discount)}`] : null),
    ["Taxable", rupees(t.taxable)],
    (inv.invoice_type === "gst" && !inv.is_interstate ? ["CGST", rupees(t.cgst)] : null),
    (inv.invoice_type === "gst" && !inv.is_interstate ? ["SGST", rupees(t.sgst)] : null),
    (inv.invoice_type === "gst" && inv.is_interstate ? ["IGST", rupees(t.igst)] : null),
    (t.round_off ? ["Round off", rupees(t.round_off)] : null),
    ["Invoice total", rupees(t.invoice_total)],
    (inv.advance_amount ? ["Advance", `- ${rupees(inv.advance_amount)}`] : null),
    ["Paid", rupees(inv.paid_amount)],
    ["Balance due", rupees(inv.balance)],
  ].filter(Boolean);
  autoTable(doc, {
    startY: endY, body: totalsBlock,
    styles: { fontSize: 9, cellPadding: 1.5 },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: "bold", halign: "right" },
      1: { cellWidth: 40, halign: "right" },
    },
    tableWidth: 80, margin: { left: 120 }, theme: "plain",
  });

  endY = doc.lastAutoTable.finalY + 8;
  if (endY < 250 && inv.notes) {
    doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text("Notes:", 10, endY); endY += 4;
    doc.setFont("helvetica", "normal");
    doc.text(String(inv.notes).slice(0, 220), 10, endY, { maxWidth: 105 });
    endY += 10;
  }
  if (endY < 250 && inv.terms) {
    doc.setFont("helvetica", "bold");
    doc.text("Terms & Conditions:", 10, endY); endY += 4;
    doc.setFont("helvetica", "normal");
    doc.text(String(inv.terms).slice(0, 220), 10, endY, { maxWidth: 105 });
  }

  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text("Customer Signature", 20, 275);
  doc.text("For " + (garage.name || "Garage"), 155, 275);
  doc.setDrawColor(150);
  doc.line(10, 273, 60, 273); doc.line(140, 273, 200, 273);

  drawFooter(doc, 1);
  return doc;
}

export const downloadInvoicePdf = (inv, garage = {}) => {
  const doc = invoicePdfV2(inv, garage);
  const reg = (inv.vehicle_snapshot?.reg_number || "vehicle").replace(/\s+/g, "_");
  doc.save(`Invoice_${inv.number}_${reg}.pdf`);
};

export function gatePassPdf(gp, garage = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a5", orientation: "portrait" });
  applyBranding(doc, garage);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DEEP_SPACE);
  doc.text("GATE PASS", 10, 34);

  doc.setFontSize(10); doc.setFont("helvetica", "normal");
  doc.text(`# ${gp.number}`, 10, 42);
  doc.text(`Issued: ${new Date(gp.issued_at || gp.created_at).toLocaleString()}`, 10, 48);

  doc.setFontSize(9); doc.setFont("helvetica", "bold");
  doc.text("Customer", 10, 60); doc.text("Vehicle", 80, 60);
  doc.setFont("helvetica", "normal");
  doc.text(String(gp.customer_snapshot?.name || "—"), 10, 66);
  doc.text(String(gp.customer_snapshot?.phone || ""), 10, 71);
  doc.text(String(gp.vehicle_snapshot?.reg_number || "—"), 80, 66);
  doc.text([gp.vehicle_snapshot?.make, gp.vehicle_snapshot?.model].filter(Boolean).join(" "), 80, 71);

  const rows = [
    ["Job Card", gp.job_card_number || "—"],
    ["Invoice", gp.invoice_number || "—"],
    ["Invoice total", rupees(gp.invoice_total)],
    ["Amount received", rupees(gp.amount_received)],
    ["Balance", rupees(gp.balance)],
    ["Payment status", String(gp.payment_status || "").toUpperCase()],
    ["Handed to", `${gp.handed_to_name || "—"} (${gp.handed_to_type || ""})`],
    ["Phone", gp.handed_to_phone || "—"],
    ["Odometer at exit", gp.odometer_at_exit != null ? `${gp.odometer_at_exit} km` : "—"],
    ["Fuel at exit", gp.fuel_at_exit || "—"],
    ["Security", gp.security_person || "—"],
  ];
  autoTable(doc, {
    startY: 82, body: rows,
    styles: { fontSize: 9, cellPadding: 1.5 },
    columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" }, 1: { cellWidth: 85 } },
    theme: "plain",
  });

  if (gp.override_used) {
    const y = doc.lastAutoTable.finalY + 4;
    doc.setTextColor(230, 57, 70);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text(`OVERRIDE: ${(gp.override_reason || "").replace(/_/g, " ").toUpperCase()}`, 10, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0);
    if (gp.override_note) doc.text(gp.override_note, 10, y + 5, { maxWidth: 130 });
  }

  doc.setFontSize(8);
  doc.text("Customer / Receiver Signature", 10, 190);
  doc.text("Security / Gate Officer", 90, 190);
  doc.setDrawColor(150);
  doc.line(10, 188, 60, 188); doc.line(90, 188, 140, 188);

  return doc;
}

export const downloadGatePassPdf = (gp, garage = {}) => {
  const doc = gatePassPdf(gp, garage);
  const reg = (gp.vehicle_snapshot?.reg_number || "vehicle").replace(/\s+/g, "_");
  doc.save(`GatePass_${gp.number}_${reg}.pdf`);
};
